<template>
  <Page>
    <div :class="$style['service']" class="container container--small">
      <NuxtLink
        :to="{
          path: `/${$lang}/service`
        }">
        {{ $t('general/customer-support') }}
      </NuxtLink>
      &nbsp;/
      <NuxtLink
        :to="{
          path: `/${$lang}/service/${currentQuestion.subject_slugs[$lang]}`
        }">
        {{ currentQuestion.subject }}
      </NuxtLink>
      &nbsp;/
      <h1 :class="$style['service__header']">
        {{ currentQuestion.question }}
      </h1>
      <div v-html="currentQuestion.answer" />
      <list class="h-margin-top-xl" bordered>
        <list-item header>
          {{ $t('partials/see-common-asked-questions') }}
        </list-item>
        <list-item
          v-for="(question, index) in otherQuestions"
          :key="index"
          :to="`/${$lang}/service/${question.slug}`">
          {{ question.question }}
        </list-item>
      </list>
      <h2
        :class="$style['service__sub-heading']"
        class="h-margin-bottom h-margin-top-xl">
        {{ $t('general/do-you-have-questions') }}
      </h2>
      <ServiceEmailTelephone />
    </div>
  </Page>
</template>
<script setup>
import { hasSpecialChars } from '~/helpers/custom-validators'
import {
  formatCanonical,
  formatOpenGraph,
  formatMetaDescription
} from '~/helpers/metadata'

const { getSubCategoryBySlug } = useNavigation()
const route = useRoute()
const { subject, answer } = route.params
const { $lang, $api } = useNuxtApp()
const navigationId = getSubCategoryBySlug('service', subject)?.id || 18 // fallback to 18  (service parent id)

if (hasSpecialChars(subject) || hasSpecialChars(answer)) {
  throw createError({
    statusCode: 404,
    message: 'Specific questions not found'
  })
}

const [{ metaData }, questions] = await Promise.all([
  $api(`navigation/${navigationId}?encoded_url=${encodeURIComponent(route.path)}`),
  $api(`faqs/${subject}`),
  useI18n(['faq', 'service'])
])

const currentQuestion = questions.find(question => question.question_slugs[$lang] === answer)
const otherQuestions = questions.filter(question => question.question_slugs[$lang] !== currentQuestion.question_slugs[$lang])

useHead({
  title: () => metaData.pageTitle,
  titleTemplate: '%s',
  link: [
    ...formatCanonical(metaData)
  ],
  meta: [
    ...formatOpenGraph(metaData.openGraph),
    formatMetaDescription(metaData)
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: metaData.schemaOrg
    }
  ]
})

</script>
<style module lang="scss">
.service {
  ol > li {
    counter-increment: foo;
    display: table-row;
    padding-bottom: $spacing;
  }

  ol > li::before {
    padding-right: $spacing;
    content: counter(foo) ". ";
    display: table-cell; /* aha! */
    text-align: right;
  }

  &__header {
    margin: $spacing * 2 0;
    font-size: $font-size-xxl;
    line-height: 3rem;

    @media (max-width: #{$breakpoint-mobile}) {
      font-size: $font-size-xl;
      line-height: 1.7rem;
    }
  }

  &__sub-heading {
    font-family: $font-heading;
    font-weight: $font-weight-bold;
    font-size: $font-size-l;
    line-height: 1;

    @media (max-width: #{$breakpoint-mobile}) {
      font-size: $font-size-m;
    }
  }
}
</style>
